import { inject, Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import { combineLatest, EMPTY, Observable, switchMap } from 'rxjs';
import { catchError, filter, map, take, tap, withLatestFrom } from 'rxjs/operators';

import { updateAppSettings } from 'app/features/settings/state/settings/settings.actions';
import {
    selectAppMode,
    selectAppSettingsIfDefined,
} from 'app/features/settings/state/settings/settings.selectors';
import { Credentials, PersonalUserPreferences, UserInfo, UserSession } from 'app/shared/models';
import { selectApiConfigState } from 'app/state/api-config/api-config.selectors';
import { loginSuccess, supervisorLogout } from 'app/state/user-session/user-session.actions';
import { selectUserSession } from 'app/state/user-session/user-session.selectors';

import {
    AppSettingsFormInput,
    areAppSettingsFormInputsEqual,
    mapAppSettingsToFormInput,
    mapFormInputToAppSettings,
} from '../models';

interface Supervisor {
    credentials: Credentials;
    userInfo: UserInfo | undefined;
    personalUserPreferences: PersonalUserPreferences;
}

export interface AppSettingsState {
    supervisor: Supervisor | undefined;
    supervisorValidated: boolean;
    preloadedSettings: AppSettingsFormInput | undefined;
    storedSettings: AppSettingsFormInput | undefined;
    formValues: Partial<AppSettingsFormInput> | undefined;
}

const initialState: AppSettingsState = {
    supervisor: undefined,
    supervisorValidated: false,
    preloadedSettings: undefined,
    storedSettings: undefined,
    formValues: undefined,
};

@Injectable()
export class AppSettingsStore extends ComponentStore<AppSettingsState> {
    private store = inject(Store);

    constructor() {
        super(initialState);

        this.preloadSettings();
        this.preloadSupervisorCredentials();
    }

    supervisor$ = this.select((state) => state.supervisor);
    supervisorValidated$ = this.select((state) => state.supervisorValidated);

    storedSettings$ = this.select((state) => state.storedSettings);
    formValues$ = this.select((state) => state.formValues);

    preloadedSettings$ = this.select((state) => state.preloadedSettings);

    isFormDirty$ = this.select(this.storedSettings$, this.formValues$, (storedSettings, formValues) => {
        if (!formValues || !storedSettings) return false;

        return !areAppSettingsFormInputsEqual(storedSettings, formValues);
    });

    preloadSupervisorCredentials = this.effect((trigger$) =>
        trigger$.pipe(
            switchMap(() => {
                return combineLatest([
                    this.supervisor$,
                    this.store.select(selectAppMode),
                    this.store.select(selectUserSession),
                ]).pipe(
                    filter(([supervisor]) => !supervisor),
                    filter(([_, appMode]) => appMode === 'multi-user'),
                    map(([_, __, userSession]) => userSession),
                    filter((userSession): userSession is UserSession => !!userSession),
                    tap(({ credentials, userInfo, data }) =>
                        this.setNotValidatedSupervisor({
                            credentials,
                            userInfo,
                            personalUserPreferences: data,
                        }),
                    ),
                    catchError(() => EMPTY),
                );
            }),
        ),
    );

    preloadSettings = this.effect((trigger$) =>
        trigger$.pipe(
            switchMap(() =>
                combineLatest([
                    this.store.pipe(selectAppSettingsIfDefined),
                    this.store.select(selectApiConfigState),
                ]).pipe(
                    take(1),
                    tap(([preloadedSettings, { identifier }]) => {
                        const preloadedFormInput = mapAppSettingsToFormInput({
                            settings: preloadedSettings,
                            identifier,
                        });
                        this.setStoredSettings(preloadedFormInput);
                        this.setPreloadedSettings(preloadedFormInput);
                    }),
                    catchError(() => EMPTY),
                ),
            ),
        ),
    );

    saveSettings = this.effect((settings$: Observable<AppSettingsFormInput>) =>
        settings$.pipe(
            withLatestFrom(
                this.supervisor$.pipe(filter((supervisor): supervisor is Supervisor => !!supervisor)),
                this.storedSettings$,
            ),
            tap(([newFormValues, supervisor, currentSettings]) => {
                this.setStoredSettings(newFormValues);

                const newAppMode = newFormValues.appMode;
                this.validateNewAppModeWithCurrentSettings(newAppMode, supervisor, currentSettings);

                const settings = mapFormInputToAppSettings(newFormValues);

                this.store.dispatch(updateAppSettings({ settings }));
            }),
            catchError(() => EMPTY),
        ),
    );

    validateNewAppModeWithCurrentSettings(
        newAppMode: string,
        supervisor: Supervisor,
        currentSettings: AppSettingsFormInput | undefined,
    ) {
        if (newAppMode !== currentSettings?.appMode) {
            if (newAppMode === 'personal') {
                this.store.dispatch(supervisorLogout());
            }
            if (newAppMode === 'multi-user') {
                const supervisorSession: UserSession = {
                    credentials: supervisor.credentials,
                    data: supervisor.personalUserPreferences,
                    startedOn: Date.now(),
                    userInfo: supervisor.userInfo,
                };
                this.store.dispatch(loginSuccess({ userSession: supervisorSession }));
            }
        }
    }

    setNotValidatedSupervisor = this.updater((state, supervisor: Supervisor) => ({
        ...state,
        supervisor,
        supervisorValidated: false,
    }));

    setValidatedSupervisor = this.updater((state, supervisor: Supervisor) => ({
        ...state,
        supervisor,
        supervisorValidated: true,
    }));

    setSupervisorCredentialsValidated = this.updater((state) => ({
        ...state,
        supervisorValidated: true,
    }));

    setPreloadedSettings = this.updater((state, preloadedSettings: AppSettingsFormInput) => ({
        ...state,
        preloadedSettings,
    }));

    setStoredSettings = this.updater((state, storedSettings: AppSettingsFormInput) => ({
        ...state,
        storedSettings,
    }));

    setFormValues = this.updater((state, formValues: Partial<AppSettingsFormInput>) => {
        return { ...state, formValues };
    });
}
